import { useQueryClient } from '@tanstack/react-query';
import { useFetchTasks } from './useFetchTaskList';
import { useFetchMatterActivities } from './useFetchMatterActivities';
import { SystemConstants } from '../constants/constants';
import { IFilterOption } from '../interface/transaction';
import { IMatter } from '../interface/matter';
import { IUser } from '../interface/user';

export enum ActivitySelectionFilters {
  ALL = 'all',
  MATTER_ACTIVITIES = 'matter_activities',
}

interface UseActivityDataProps {
  selectedFilter: IFilterOption | undefined;
  searchValue: string;
  linkedMatter: IMatter | undefined;
  user: IUser | null | undefined;
}

export const useActivityData = ({
  selectedFilter,
  searchValue,
  linkedMatter,
  user,
}: UseActivityDataProps) => {
  const queryClient = useQueryClient();

  const {
    data: paginatedActivitiesData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch: refetchActivities,
  } = useFetchTasks({
    extraFilters: {
      responsibleUserId: user?.id,
      activityTypeId: SystemConstants.TASK_ID,
    },
    pageSize: 50,
    search: searchValue,
    relations: [
      'status',
      'matter:subject,matterNumber,invoiceableType',
      'matter.principalCompany:name',
      'matter.principalPrivatePerson:fullName',
    ],
    enabled: selectedFilter?.value === ActivitySelectionFilters.ALL,
  });

  const {
    data: paginatedMatterActivityData,
    isLoading: isMatterActivitiesLoading,
    isFetchingNextPage: isMatterActivitiesFetchingNextPage,
    fetchNextPage: fetchMatterActivitiesNextPage,
    hasNextPage: hasMatterActivitiesNextPage,
    isFetching: isMatterActivitiesFetching,
    refetch: refetchMatterActivities,
  } = useFetchMatterActivities({
    pageSize: 50,
    relations: [
      'status',
      'matter:subject,matterNumber,invoiceableType',
      'matter.principalCompany:name',
      'matter.principalPrivatePerson:fullName',
    ],
    search: searchValue,
    enabled:
      !!linkedMatter?.id && selectedFilter?.value === ActivitySelectionFilters.MATTER_ACTIVITIES,
    id: linkedMatter?.id ?? '',
  });

  const isMatterActivities = selectedFilter?.value === ActivitySelectionFilters.MATTER_ACTIVITIES;

  const refreshActivityList = () => {
    if (isMatterActivities) {
      queryClient.removeQueries({ queryKey: ['matter-activities-list'] });
      return refetchMatterActivities();
    }
    queryClient.removeQueries({ queryKey: ['tasks-list'] });
    return refetchActivities();
  };

  return {
    data: isMatterActivities ? paginatedMatterActivityData : paginatedActivitiesData,
    isLoading: isMatterActivities ? isMatterActivitiesLoading : isLoading,
    isFetchingNextPage: isMatterActivities
      ? isMatterActivitiesFetchingNextPage
      : isFetchingNextPage,
    fetchNextPage: isMatterActivities ? fetchMatterActivitiesNextPage : fetchNextPage,
    hasNextPage: isMatterActivities ? hasMatterActivitiesNextPage : hasNextPage,
    isFetching: isMatterActivities ? isMatterActivitiesFetching : isFetching,
    refreshActivityList,
  };
};
